/**
 * DeliberationReplay - Replay Fiel de Deliberação
 * 
 * Renderiza timeline baseada em eventos (seq).
 * Nunca recomputa. Apenas exibe.
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDeliberationDetail } from '../services/deliberationService';
import type { DeliberationDetail, DeliberationEvent } from '../services/deliberationService';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function DeliberationReplay() {
    const { runId } = useParams<{ runId: string }>();
    const navigate = useNavigate();
    const [detail, setDetail] = useState<DeliberationDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set());

    useEffect(() => {
        if (runId) {
            loadDetail(runId);
        }
    }, [runId]);

    async function loadDetail(id: string) {
        try {
            setLoading(true);
            const data = await getDeliberationDetail(id);
            setDetail(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erro ao carregar deliberação');
        } finally {
            setLoading(false);
        }
    }

    function toggleEvent(seq: number) {
        const newExpanded = new Set(expandedEvents);
        if (newExpanded.has(seq)) {
            newExpanded.delete(seq);
        } else {
            newExpanded.add(seq);
        }
        setExpandedEvents(newExpanded);
    }

    function handleRerun() {
        if (!detail) return;

        // Redirecionar para nova deliberação com dados pré-preenchidos
        navigate('/deliberate/new', {
            state: {
                question: detail.run.question,
                context: detail.run.context || '',
            }
        });
    }

    function getEventIcon(type: string) {
        const icons = {
            stage_started: '▶️',
            stage_completed: '✅',
            error: '❌',
            done: '🎯',
            degraded: '⚠️',
        };
        return icons[type as keyof typeof icons] || '•';
    }

    function getEventColor(type: string) {
        const colors = {
            stage_started: 'border-blue-200 bg-blue-50',
            stage_completed: 'border-green-200 bg-green-50',
            error: 'border-red-200 bg-red-50',
            done: 'border-purple-200 bg-purple-50',
            degraded: 'border-yellow-200 bg-yellow-50',
        };
        return colors[type as keyof typeof colors] || 'border-gray-200 bg-gray-50';
    }

    function renderEventPayload(event: DeliberationEvent) {
        if (!expandedEvents.has(event.seq)) return null;

        return (
            <div className="mt-3 p-4 bg-white rounded border border-gray-200">
                <pre className="text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto">
                    {JSON.stringify(event.payload, null, 2)}
                </pre>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Carregando replay...</p>
                </div>
            </div>
        );
    }

    if (error || !detail) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <p className="text-red-600">{error || 'Deliberação não encontrada'}</p>
                    <button
                        onClick={() => navigate('/deliberations')}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Voltar ao histórico
                    </button>
                </div>
            </div>
        );
    }

    const { run, events } = detail;

    return (
        <div className="max-w-4xl mx-auto p-6">
            {/* Header */}
            <div className="mb-8">
                <button
                    onClick={() => navigate('/deliberations')}
                    className="text-blue-600 hover:text-blue-700 mb-4 flex items-center gap-2"
                >
                    ← Voltar ao histórico
                </button>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                            <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                {run.question}
                            </h1>
                            {run.context && (
                                <p className="text-gray-600 text-sm">{run.context}</p>
                            )}
                        </div>
                        <button
                            onClick={handleRerun}
                            className="ml-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 whitespace-nowrap"
                        >
                            🔄 Reexecutar
                        </button>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>Status: <strong className="text-gray-900">{run.status}</strong></span>
                        <span>Criado: {format(new Date(run.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</span>
                        {run.confidence !== undefined && (
                            <span>Confiança: <strong className="text-gray-900">{(run.confidence * 100).toFixed(0)}%</strong></span>
                        )}
                    </div>
                </div>
            </div>

            {/* Timeline */}
            <div className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Timeline de Execução ({events.length} eventos)
                </h2>

                {events.map((event, index) => (
                    <div
                        key={event.id}
                        className={`border-l-4 pl-4 py-3 rounded-r ${getEventColor(event.type)}`}
                    >
                        <div
                            className="flex items-start justify-between cursor-pointer"
                            onClick={() => toggleEvent(event.seq)}
                        >
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-lg">{getEventIcon(event.type)}</span>
                                    <span className="font-semibold text-gray-900">
                                        {event.stage || event.type}
                                    </span>
                                    <span className="text-xs text-gray-500">#{event.seq}</span>
                                </div>
                                <p className="text-sm text-gray-600">
                                    {format(new Date(event.timestamp), "HH:mm:ss", { locale: ptBR })}
                                </p>
                            </div>
                            <button className="text-gray-400 hover:text-gray-600">
                                {expandedEvents.has(event.seq) ? '▼' : '▶'}
                            </button>
                        </div>

                        {renderEventPayload(event)}

                        {/* Linha conectora (exceto último) */}
                        {index < events.length - 1 && (
                            <div className="h-4 border-l-2 border-gray-300 ml-2 mt-2"></div>
                        )}
                    </div>
                ))}
            </div>

            {/* Resumo Final */}
            {run.final_decision && (
                <div className="mt-8 bg-purple-50 border border-purple-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-purple-900 mb-3">
                        Decisão Final
                    </h3>
                    <p className="text-gray-800 mb-4">{run.final_decision}</p>

                    {run.action_plan && run.action_plan.length > 0 && (
                        <div>
                            <h4 className="font-semibold text-purple-900 mb-2">Plano de Ação:</h4>
                            <ol className="list-decimal list-inside space-y-1">
                                {run.action_plan.map((action, i) => (
                                    <li key={i} className="text-gray-700">{action}</li>
                                ))}
                            </ol>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
